/**
 * useGoogie — the redesign switch, now welded on.
 *
 * GOOGIE-15. The Googie v7 redesign shipped screen by screen behind a
 * preference so a studio could flip back mid-task while it was half
 * built. It isn't half built any more: Today, Projects, Project
 * detail, Contract Studio, Clients, Calendar, Inquiries, Deliveries,
 * Galleries, Emails, Expenses, Forms, Nova and Settings are all
 * redesigned, so the old bodies for those screens are no longer a
 * fallback anyone should land on.
 *
 * This hook therefore reports `true` unconditionally and the
 * Appearance toggle is gone. It keeps its `[value, set]` shape so the
 * nineteen call sites need no edit, and the old code paths behind
 * `if (googieOn)` stay in the tree — unreachable, but one line from
 * coming back if the redesign has to be pulled in a hurry.
 *
 * Nine screens have no Googie version yet — Billing, Learn, Sites,
 * the gallery editor, delivery detail, the client portal, Galaxy,
 * Settings → Templates and Intake → new. They are all reached from
 * inside a Googie screen, so they read as detail views rather than a
 * second app, and they keep their current look until they're
 * redesigned in turn.
 *
 * The storage key is still written so a later revert can read back
 * whatever a studio had chosen, and so the iOS app's parity read
 * doesn't see the key vanish.
 */
import { useCallback } from 'react';

const STORAGE_KEY = 'orbit.googie';

export function useGoogie(): [boolean, (next: boolean) => void] {
  // No state and no storage read any more — there is nothing left to
  // derive. This calls one hook where the old version called three;
  // that is safe because a deploy replaces the bundle and the app
  // remounts from scratch, so no component ever sees the old hook
  // order and the new one in the same mounted lifetime.
  const set = useCallback((next: boolean) => {
    // The preference is recorded but no longer consulted. Kept so a
    // revert has the studio's original answer to go back to.
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* private mode — nothing to record, nothing to break */
    }
  }, []);

  return [true, set];
}

export default useGoogie;
